import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import SearchIcon from '@mui/icons-material/Search';
import SpeedIcon from '@mui/icons-material/Speed';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { DESIGN_TOKENS } from '../styles/theme';

// ============================================================
// STATISTICS DATA
// ============================================================

const PLATFORM_STATISTICS = [
  { value: '2 400+', label: 'Zarejestrowanych firm' },
  { value: '8 900+', label: 'Pilotów w bazie' },
  { value: '15 000+', label: 'Ogłoszeń miesięcznie' },
  { value: '98%', label: 'Zadowolonych kontrahentów' },
];

// ============================================================
// FEATURES DATA
// ============================================================

const PLATFORM_FEATURES = [
  {
    icon: <SearchIcon sx={{ fontSize: 28 }} />,
    title: 'Szybkie wyszukiwanie',
    description:
      'Znajdź pilota, firmę lub ogłoszenie w kilka sekund. Zaawansowane filtry skracają czas poszukiwań.',
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 28 }} />,
    title: 'Weryfikacja kontrahentów',
    description:
      'Każda firma przechodzi proces weryfikacji. Wiesz, z kim współpracujesz, zanim podpiszesz umowę.',
  },
  {
    icon: <HandshakeIcon sx={{ fontSize: 28 }} />,
    title: 'Bezpośredni kontakt',
    description:
      'Bez pośredników. Kontaktuj się bezpośrednio z firmami i pilotami, negocjuj warunki sam.',
  },
  {
    icon: <SpeedIcon sx={{ fontSize: 28 }} />,
    title: 'Ogłoszenia w czasie rzeczywistym',
    description:
      'Aktualne oferty pracy i zlecenia transportowe dostępne natychmiast po publikacji.',
  },
];

// ============================================================
// HOW IT WORKS STEPS
// ============================================================

const HOW_IT_WORKS_STEPS = [
  {
    stepNumber: '01',
    title: 'Utwórz profil',
    description: 'Zarejestruj firmę lub profil pilota. Zajmuje to mniej niż 5 minut.',
  },
  {
    stepNumber: '02',
    title: 'Dodaj ogłoszenie',
    description: 'Opublikuj zlecenie lub ofertę z dokładnymi parametrami przejazdu.',
  },
  {
    stepNumber: '03',
    title: 'Nawiąż współpracę',
    description: 'Wybierz najlepszą ofertę i skontaktuj się bezpośrednio z wykonawcą.',
  },
];

// ============================================================
// HERO SECTION
// ============================================================

function HeroSection() {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primaryLighter} 0%, ${DESIGN_TOKENS.colors.surface} 60%)`,
        pt: { xs: 8, md: 12 },
        pb: { xs: 8, md: 12 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -80,
          right: -80,
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${DESIGN_TOKENS.colors.primaryLighter}80 0%, transparent 70%)`,
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -40,
          left: '30%',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${DESIGN_TOKENS.colors.accentLight}60 0%, transparent 70%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Chip
              label="Platforma transportowa nr 1 w Polsce"
              size="small"
              sx={{
                backgroundColor: DESIGN_TOKENS.colors.accentLight,
                color: DESIGN_TOKENS.colors.accentDark,
                fontWeight: 700,
                fontSize: '0.75rem',
                mb: 3,
                border: `1px solid ${DESIGN_TOKENS.colors.accent}40`,
              }}
            />
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.25rem', md: '3.5rem', lg: '4rem' },
                lineHeight: 1.1,
                mb: 3,
              }}
            >
              Transport ciężki{' '}
              <Box
                component="span"
                sx={{
                  color: DESIGN_TOKENS.colors.primary,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 2,
                    left: 0,
                    right: 0,
                    height: 4,
                    backgroundColor: DESIGN_TOKENS.colors.accent,
                    borderRadius: 2,
                    opacity: 0.6,
                  },
                }}
              >
                połączony
              </Box>{' '}
              w jednym miejscu
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                maxWidth: 480,
                mb: 4,
                lineHeight: 1.7,
              }}
            >
              Platforma dla firm transportowych, pilotów i kierowców. Znajdź
              zlecenie, partnera lub pracownika — bez zbędnego pośrednictwa.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                component={RouterLink}
                to="/listings"
                variant="contained"
                color="primary"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{ px: 4, py: 1.5, fontSize: '1rem' }}
              >
                Przeglądaj ogłoszenia
              </Button>
              <Button
                component={RouterLink}
                to="/companies"
                variant="outlined"
                color="primary"
                size="large"
                sx={{ px: 4, py: 1.5, fontSize: '1rem' }}
              >
                Katalog firm
              </Button>
            </Box>
          </Grid>

          {!isMobileView && (
            <Grid item md={6}>
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                {/* Abstract transport illustration using CSS */}
                <Box
                  sx={{
                    width: 480,
                    height: 360,
                    position: 'relative',
                    background: `linear-gradient(145deg, ${DESIGN_TOKENS.colors.primaryLighter}, ${DESIGN_TOKENS.colors.surface})`,
                    borderRadius: '24px',
                    border: `2px solid ${DESIGN_TOKENS.colors.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    boxShadow: DESIGN_TOKENS.shadows.elevated,
                  }}
                >
                  {/* Road lines */}
                  <Box sx={{
                    position: 'absolute',
                    bottom: 60,
                    left: 0,
                    right: 0,
                    height: 2,
                    background: `repeating-linear-gradient(90deg, ${DESIGN_TOKENS.colors.primaryLight} 0px, ${DESIGN_TOKENS.colors.primaryLight} 40px, transparent 40px, transparent 70px)`,
                    opacity: 0.4,
                  }} />
                  <Box sx={{
                    position: 'absolute',
                    bottom: 80,
                    left: 0,
                    right: 0,
                    height: 2,
                    background: `repeating-linear-gradient(90deg, ${DESIGN_TOKENS.colors.primaryLight} 0px, ${DESIGN_TOKENS.colors.primaryLight} 40px, transparent 40px, transparent 70px)`,
                    opacity: 0.2,
                  }} />
                  <LocalShippingIcon
                    sx={{
                      fontSize: 140,
                      color: DESIGN_TOKENS.colors.primary,
                      opacity: 0.12,
                      position: 'absolute',
                    }}
                  />
                  {/* Stats overlay cards */}
                  <Box sx={{ position: 'relative', zIndex: 1, p: 3, width: '100%' }}>
                    {PLATFORM_STATISTICS.slice(0, 2).map((stat) => (
                      <Box
                        key={stat.label}
                        sx={{
                          backgroundColor: `${DESIGN_TOKENS.colors.surface}E8`,
                          backdropFilter: 'blur(8px)',
                          borderRadius: '12px',
                          border: `1px solid ${DESIGN_TOKENS.colors.border}`,
                          p: 2,
                          mb: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          boxShadow: DESIGN_TOKENS.shadows.card,
                        }}
                      >
                        <Typography variant="h4" color="primary" fontWeight={700}>
                          {stat.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {stat.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
}

// ============================================================
// STATISTICS STRIP
// ============================================================

function StatisticsStrip() {
  return (
    <Box
      sx={{
        borderTop: `1px solid ${DESIGN_TOKENS.colors.border}`,
        borderBottom: `1px solid ${DESIGN_TOKENS.colors.border}`,
        backgroundColor: DESIGN_TOKENS.colors.surface,
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={2} justifyContent="center">
          {PLATFORM_STATISTICS.map((statItem) => (
            <Grid item xs={6} sm={3} key={statItem.label}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h3"
                  sx={{ color: DESIGN_TOKENS.colors.primary, fontWeight: 700 }}
                >
                  {statItem.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {statItem.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// ============================================================
// FEATURES SECTION
// ============================================================

function FeaturesSection() {
  return (
    <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: DESIGN_TOKENS.colors.background }}>
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', mb: 7 }}>
          <Typography variant="h2" gutterBottom>
            Dlaczego QPRO Connect?
          </Typography>
          <Typography variant="subtitle1" sx={{ maxWidth: 560, mx: 'auto' }}>
            Stworzyliśmy narzędzie, które rzeczywiście działa w transporcie ciężkim.
            Bez zbędnych funkcji, z tym co potrzebujesz.
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {PLATFORM_FEATURES.map((featureItem) => (
            <Grid item xs={12} sm={6} lg={3} key={featureItem.title}>
              <Card
                sx={{
                  height: '100%',
                  p: 1,
                  cursor: 'default',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: DESIGN_TOKENS.shadows.cardHover,
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '12px',
                      backgroundColor: DESIGN_TOKENS.colors.primaryLighter,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                      color: DESIGN_TOKENS.colors.primary,
                    }}
                  >
                    {featureItem.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom fontWeight={600}>
                    {featureItem.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.65}>
                    {featureItem.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// ============================================================
// HOW IT WORKS SECTION
// ============================================================

function HowItWorksSection() {
  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: `linear-gradient(180deg, ${DESIGN_TOKENS.colors.primaryLighter}40 0%, ${DESIGN_TOKENS.colors.surface} 100%)`,
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', mb: 7 }}>
          <Typography variant="h2" gutterBottom>
            Jak to działa?
          </Typography>
          <Typography variant="subtitle1" sx={{ maxWidth: 480, mx: 'auto' }}>
            Trzy kroki dzielą Cię od pierwszego zlecenia.
          </Typography>
        </Box>
        <Grid container spacing={4} justifyContent="center">
          {HOW_IT_WORKS_STEPS.map((stepItem, stepIndex) => (
            <Grid item xs={12} md={4} key={stepItem.stepNumber}>
              <Box sx={{ textAlign: 'center', position: 'relative' }}>
                {stepIndex < HOW_IT_WORKS_STEPS.length - 1 && (
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      position: 'absolute',
                      top: 30,
                      right: '-8%',
                      width: '16%',
                      height: 2,
                      background: `linear-gradient(90deg, ${DESIGN_TOKENS.colors.primaryLight}, transparent)`,
                    }}
                  />
                )}
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primary}, ${DESIGN_TOKENS.colors.primaryLight})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    boxShadow: `0 4px 20px ${DESIGN_TOKENS.colors.primary}40`,
                  }}
                >
                  <Typography
                    sx={{
                      color: '#FFFFFF',
                      fontWeight: 800,
                      fontSize: '1.1rem',
                      fontFamily: DESIGN_TOKENS.typography.fontFamilyDisplay,
                    }}
                  >
                    {stepItem.stepNumber}
                  </Typography>
                </Box>
                <Typography variant="h5" gutterBottom fontWeight={600}>
                  {stepItem.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {stepItem.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// ============================================================
// CTA SECTION
// ============================================================

function CallToActionSection() {
  return (
    <Box sx={{ py: { xs: 8, md: 12 } }}>
      <Container maxWidth="md">
        <Box
          sx={{
            background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primary} 0%, ${DESIGN_TOKENS.colors.primaryDark} 100%)`,
            borderRadius: '24px',
            p: { xs: 5, md: 8 },
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: DESIGN_TOKENS.shadows.elevated,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -40,
              right: -40,
              width: 200,
              height: 200,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.06)',
            },
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h2"
              sx={{ color: '#FFFFFF', fontSize: { xs: '1.75rem', md: '2.5rem' }, mb: 2 }}
            >
              Gotowy na nowe zlecenia?
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'rgba(255,255,255,0.85)', mb: 4, fontSize: '1.0625rem' }}
            >
              Dołącz do tysięcy firm i specjalistów, którzy już korzystają z QPRO Connect.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Button
                component={RouterLink}
                to="/register"
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: DESIGN_TOKENS.colors.accent,
                  color: DESIGN_TOKENS.colors.textPrimary,
                  px: 4,
                  py: 1.5,
                  '&:hover': { backgroundColor: DESIGN_TOKENS.colors.accentDark },
                }}
              >
                Zarejestruj się za darmo
              </Button>
              <Button
                component={RouterLink}
                to="/listings"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: '#FFFFFF',
                  px: 4,
                  py: 1.5,
                  '&:hover': { borderColor: '#FFFFFF', backgroundColor: 'rgba(255,255,255,0.08)' },
                }}
              >
                Przeglądaj ogłoszenia
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

// ============================================================
// CATEGORY TILES SECTION
// ============================================================

const CATEGORY_TILES = [
  {
    title: 'Firmy transportowe',
    description: 'Baza przewoźników z całej Polski',
    icon: <LocalShippingIcon sx={{ fontSize: 40 }} />,
    path: '/companies/transport-companies',
    count: '2 400+',
  },
  {
    title: 'Piloci transportu',
    description: 'Certyfikowani piloci ponadnormatywnych',
    icon: <PeopleIcon sx={{ fontSize: 40 }} />,
    path: '/companies/pilots',
    count: '8 900+',
  },
];

function CategoryTilesSection() {
  return (
    <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: DESIGN_TOKENS.colors.surface }}>
      <Container maxWidth="xl">
        <Box sx={{ mb: 5 }}>
          <Typography variant="h2" gutterBottom>
            Znajdź to, czego szukasz
          </Typography>
          <Typography variant="subtitle1">
            Przeglądaj firmy i specjalistów w naszym katalogu.
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {CATEGORY_TILES.map((categoryTile) => (
            <Grid item xs={12} sm={6} key={categoryTile.title}>
              <Card
                component={RouterLink}
                to={categoryTile.path}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  p: 3,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.surface}, ${DESIGN_TOKENS.colors.primaryLighter}60)`,
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: DESIGN_TOKENS.shadows.cardHover,
                    textDecoration: 'none',
                    '& .category-arrow': { transform: 'translateX(4px)' },
                  },
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 72,
                    height: 72,
                    borderRadius: '16px',
                    backgroundColor: DESIGN_TOKENS.colors.primaryLighter,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: DESIGN_TOKENS.colors.primary,
                  }}
                >
                  {categoryTile.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" fontWeight={600}>
                    {categoryTile.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {categoryTile.description}
                  </Typography>
                  <Chip
                    label={categoryTile.count}
                    size="small"
                    sx={{
                      mt: 1,
                      backgroundColor: DESIGN_TOKENS.colors.primaryLighter,
                      color: DESIGN_TOKENS.colors.primary,
                      fontWeight: 700,
                    }}
                  />
                </Box>
                <ArrowForwardIcon
                  className="category-arrow"
                  sx={{
                    color: DESIGN_TOKENS.colors.primary,
                    transition: 'transform 0.2s ease',
                    flexShrink: 0,
                  }}
                />
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// ============================================================
// HOME PAGE ASSEMBLY
// ============================================================

export default function HomePage() {
  return (
    <>
      <HeroSection />
      <StatisticsStrip />
      <FeaturesSection />
      <CategoryTilesSection />
      <HowItWorksSection />
      <CallToActionSection />
    </>
  );
}
